/**
 * A single facet checkbox row: [box] label [count].
 * Styling per mockup_2.dc.html (facets §): label flex gap 9px, padding 4px 0;
 * box 15px r4 border var(--control-border) (checked → bg var(--accent-hex) white ✓ 10px); text flex-1
 * 12.5px var(--text-1) (checked → var(--text-strong)/500); count mono 11px var(--text-muted) (checked → var(--text-3)).
 */

interface FacetCheckboxProps {
  label: string;
  /** Underlying option value (id or string); passed back to onToggle. */
  value: string;
  /** Live count for this option given the other applied facets; null while loading. */
  count: number | null;
  checked: boolean;
  onToggle: (value: string) => void;
}

function formatCount(n: number): string {
  // mono digits with thin-space grouping, matching mockup "18 204".
  return n.toLocaleString("ru-RU").replace(/ /g, " ");
}

export function FacetCheckbox({ label, value, count, checked, onToggle }: FacetCheckboxProps) {
  return (
    <div
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      tabIndex={0}
      onClick={() => onToggle(value)}
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          onToggle(value);
        }
      }}
      className="rounded-[4px] outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-hex)]"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "4px 0",
        fontSize: 12.5,
        cursor: "pointer",
        color: checked ? "var(--text-strong)" : "var(--text-1)",
        fontWeight: checked ? 500 : 400,
      }}
    >
      {checked ? (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 15,
            height: 15,
            borderRadius: 4,
            background: "var(--accent-hex)",
            color: "var(--on-accent)",
            fontSize: 10,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          ✓
        </span>
      ) : (
        <span
          style={{
            width: 15,
            height: 15,
            borderRadius: 4,
            border: "1.5px solid var(--control-border)",
            background: "var(--panel)",
            flexShrink: 0,
          }}
        />
      )}
      <span
        style={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
      <span
        className="font-mono"
        style={{ fontSize: 11, color: checked ? "var(--text-3)" : "var(--text-muted)", flexShrink: 0 }}
      >
        {count == null ? "…" : formatCount(count)}
      </span>
    </div>
  );
}
